import React from "react";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Store,
  CheckCircle2,
  CircleDashed,
  XCircle,
  Loader2,
  Unplug,
  Trash2,
  MessageCircle,
  ArrowRightLeft,
} from "lucide-react";
import { cn } from "../../../../utils/cn";
import WaConnectionDiagnostics from "./WaConnectionDiagnostics";

const STATUS_META = {
  open: {
    labelKey: "Conectado",
    Icon: CheckCircle2,
    chip: "bg-status-success-soft text-status-success",
  },
  connecting: {
    labelKey: "Conectando",
    Icon: CircleDashed,
    chip: "bg-status-warning-soft text-status-warning",
  },
  close: {
    labelKey: "Desconectado",
    Icon: XCircle,
    chip: "bg-status-error-soft text-status-error",
  },
};

// Opciones de propósito del número. Un número puede servir a las notificaciones
// operativas a grupos, a la mesa de ayuda (conversar), o a ambas cosas.
const PURPOSE_OPTIONS = [
  { value: "both", labelKey: "Ambos" },
  { value: "notifications", labelKey: "Notificaciones" },
  { value: "helpdesk", labelKey: "Mesa de ayuda" },
];

const PURPOSE_HINTS = {
  both: "Atiende conversaciones y envía avisos a un grupo.",
  notifications: "Solo envía avisos a un grupo. No aparece en la mesa de ayuda.",
  helpdesk: "Solo atiende conversaciones en la mesa de ayuda.",
};

export default function UnofficialWaCard({
  integration,
  groups,
  onDisconnect,
  onDeletePermanent,
  // Retirar o migrar el número (bloqueado / dado de baja). Ausente = el usuario
  // no puede hacerlo (supervisor o administrador).
  onRetire,
  onAssignGroup,
  onSetPurpose,
  deletingId,
  opsGroups,
  onLoadOpsGroups,
  onSetOpsGroup,
  loadingOpsGroups,
  savingOpsGroup,
  savingPurpose,
  // Historial de conexión desplegable: solo quien administra los números.
  showConnectionHistory = false,
}) {
  const { t } = useTranslation();
  const statusMeta = STATUS_META[integration.wa_connection_status] || STATUS_META.close;
  const StatusIcon = statusMeta.Icon;
  const group = groups?.find((g) => g.id === integration.business_group_id);
  const isConnected = integration.wa_connection_status === "open";
  const isDeleting = deletingId === integration.id;
  // Registros previos a este campo se interpretan como "both" (retro-compat).
  const purpose = integration.purpose || "both";
  const isNotifCapable = purpose === "notifications" || purpose === "both";
  const isHelpdeskCapable = purpose === "helpdesk" || purpose === "both";
  const currentOpsJid = integration.ops_notification_group_jid || "";
  const currentOpsName = integration.ops_notification_group_name || "";
  // Asegura que el grupo actualmente configurado aparezca en la lista (con su
  // nombre guardado) aunque Evolution no lo devuelva (p. ej. el bot ya no está
  // en él).
  const opsOptions =
    currentOpsJid && opsGroups && !opsGroups.some((g) => g.jid === currentOpsJid)
      ? [{ jid: currentOpsJid, name: currentOpsName || currentOpsJid }, ...opsGroups]
      : opsGroups || [];

  return (
    <div className="flex flex-col gap-2 px-3 py-2.5 rounded-md border border-border bg-bg hover:border-fg-subtle transition-colors">
      {/* Línea principal: nombre + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg truncate" title={integration.display_name}>
            {integration.display_name || t("Sin nombre")}
          </div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[12px] md:text-[11px] text-fg-muted">
            {integration.phone_number && (
              <span className="inline-flex items-center gap-1">
                <Phone className="w-3 h-3 text-fg-subtle" aria-hidden="true" />
                {integration.phone_number}
              </span>
            )}
            <span
              className={cn(
                "inline-flex items-center gap-1 px-1.5 h-5 md:h-4 rounded-full text-[12px] md:text-[10px] font-medium",
                statusMeta.chip,
              )}
            >
              <StatusIcon
                className={cn(
                  "w-2.5 h-2.5",
                  integration.wa_connection_status === "connecting" && "animate-spin",
                )}
                aria-hidden="true"
              />
              {t(statusMeta.labelKey)}
            </span>
            {group && (
              <span className="inline-flex items-center gap-1">
                <Store className="w-3 h-3 text-fg-subtle" aria-hidden="true" />
                {group.name}
              </span>
            )}
            {isHelpdeskCapable && (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 md:h-4 rounded-full text-[12px] md:text-[10px] font-medium bg-brand-50 text-brand-700">
                <MessageCircle className="w-2.5 h-2.5" aria-hidden="true" />
                {t("Mesa de ayuda")}
              </span>
            )}
          </div>
        </div>

        {/* Acciones: Desconectar si está conectado (abre modal de confirmación);
            Eliminar si está desconectado (sin modal). */}
        <div className="flex items-center gap-1 shrink-0">
          {onRetire && (
            <button
              type="button"
              onClick={() => onRetire(integration)}
              title={t("Retirar o migrar número")}
              aria-label={t("Retirar o migrar número")}
              className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          )}
          {isConnected ? (
            <button
              type="button"
              onClick={() => onDisconnect?.(integration)}
              title={t("Desconectar")}
              aria-label={t("Desconectar instancia")}
              className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-status-error-soft hover:text-status-error transition-colors"
            >
              <Unplug className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onDeletePermanent?.(integration.id)}
              disabled={isDeleting}
              title={t("Eliminar")}
              aria-label={t("Eliminar número")}
              className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-status-error-soft hover:text-status-error disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Por qué se cayó la sesión (bloqueo, sesión cerrada, otra sesión…). */}
      <WaConnectionDiagnostics
        integration={integration}
        isConnected={isConnected}
        showHistory={showConnectionHistory}
      />

      {/* Propósito del número: notificaciones a grupos, mesa de ayuda, o ambos.
          Es lo que mantiene separado el uso de cada WhatsApp. */}
      <div className="border-t border-border pt-2">
        <div className="text-[12px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">
          {t("Uso de este número")}
        </div>
        {/* A6 · A 360px la fila de tres segmentos mide más que la tarjeta (230px
            útiles) y el `overflow-hidden` del grupo cortaba "Notificaciones"
            —92px de palabra sin punto de corte— a media palabra. Bajo md los
            tres van apilados a todo el ancho, 44px cada uno; en ≥768px vuelve
            al inline-flex horizontal de siempre. */}
        <div
          className="grid grid-cols-1 w-full md:inline-flex md:w-auto rounded-md border border-border overflow-hidden"
          role="group"
          aria-label={t("Uso de este número")}
        >
          {PURPOSE_OPTIONS.map((opt, idx) => {
            const active = purpose === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={savingPurpose || active}
                onClick={() => onSetPurpose?.(integration.id, opt.value)}
                className={cn(
                  "px-1.5 md:px-2.5 min-h-[44px] md:min-h-0 md:h-7 text-[12px] md:text-xs leading-tight font-medium transition-colors disabled:cursor-default",
                  idx > 0 && "border-t md:border-t-0 md:border-l border-border",
                  active
                    ? "hd-brand-solid bg-brand-500 text-white"
                    : "bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-100",
                )}
              >
                {t(opt.labelKey)}
              </button>
            );
          })}
          {savingPurpose && (
            <span className="inline-flex items-center px-2">
              <Loader2 className="w-3 h-3 animate-spin text-fg-muted" aria-hidden="true" />
            </span>
          )}
        </div>
        <p className="mt-1 text-[12px] md:text-[10px] text-fg-subtle leading-relaxed">
          {t(PURPOSE_HINTS[purpose])}
        </p>
      </div>

      {/* Selector de grupo (solo si hay grupos disponibles) */}
      {groups?.length > 0 && (
        <div className="flex items-center gap-2">
          <label
            htmlFor={`group-${integration.id}`}
            className="text-[12px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle shrink-0"
          >
            {t("Grupo")}
          </label>
          <select
            id={`group-${integration.id}`}
            value={integration.business_group_id || ""}
            onChange={(e) => onAssignGroup?.(integration.id, e.target.value)}
            className="flex-1 h-7 px-2 rounded-md border border-border bg-bg text-xs text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">{t("Sin grupo")}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Grupo de notificaciones automáticas (recolectados / top en ruta).
          Solo aplica a números habilitados para notificaciones. OJO: distinto
          del "Grupo" de arriba (business group organizativo); esto es un grupo
          de WhatsApp real "@g.us" que recibe los avisos. */}
      {isNotifCapable && (
      <div className="border-t border-border pt-2">
        <div className="text-[12px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">
          {t("Grupo de notificaciones automáticas")}
        </div>

        {!isConnected ? (
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t("Conecta la instancia para elegir el grupo de avisos.")}
          </p>
        ) : opsGroups == null ? (
          <button
            type="button"
            onClick={() => onLoadOpsGroups?.(integration.id)}
            disabled={loadingOpsGroups}
            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-border text-xs text-fg-muted hover:text-fg hover:bg-bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loadingOpsGroups && (
              <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
            )}
            {currentOpsJid ? t("Cambiar grupo de avisos") : t("Elegir grupo de avisos")}
          </button>
        ) : (
          <div className="space-y-1">
            {opsOptions.length === 0 ? (
              <p className="text-[12px] md:text-[11px] text-fg-muted leading-relaxed">
                {t(
                  "No se ven grupos para este número. Crea el grupo en WhatsApp, agrega este número y envía un mensaje en el grupo; luego pulsa Recargar.",
                )}
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  value={currentOpsJid}
                  onChange={(e) => {
                    const opt = opsOptions.find((g) => g.jid === e.target.value);
                    onSetOpsGroup?.(integration.id, e.target.value, opt?.name);
                  }}
                  disabled={savingOpsGroup}
                  className="flex-1 h-7 px-2 rounded-md border border-border bg-bg text-xs text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
                >
                  <option value="">{t("Sin grupo (no enviar)")}</option>
                  {opsOptions.map((g) => (
                    <option key={g.jid} value={g.jid}>
                      {g.name || g.jid}
                    </option>
                  ))}
                </select>
                {savingOpsGroup && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-fg-muted" aria-hidden="true" />
                )}
              </div>
            )}

            {/* Recargar: tras agregar el número a un grupo en WhatsApp, refresca
                la lista sin recargar la página. */}
            <button
              type="button"
              onClick={() => onLoadOpsGroups?.(integration.id)}
              disabled={loadingOpsGroups}
              className="inline-flex items-center gap-1 min-h-[44px] md:min-h-0 text-[12px] md:text-[10px] text-fg-subtle hover:text-fg disabled:opacity-50 transition-colors"
            >
              {loadingOpsGroups && (
                <Loader2 className="w-2.5 h-2.5 animate-spin" aria-hidden="true" />
              )}
              {t("Recargar grupos")}
            </button>
          </div>
        )}

        {currentOpsJid && (
          <p className="mt-1 text-[12px] md:text-[10px] text-fg-subtle truncate" title={currentOpsJid}>
            {t("Actual")}: {currentOpsName || currentOpsJid}
          </p>
        )}
      </div>
      )}
    </div>
  );
}
